import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PlusCircle, Clock, CheckCircle, AlertCircle, ArrowRight, Ticket } from 'lucide-react';
import api from '../../services/api';

const statusColors = {
  'Yeni': 'bg-blue-100 text-blue-700',
  'İşleme Alındı': 'bg-purple-100 text-purple-700',
  'Devam Ediyor': 'bg-amber-100 text-amber-700',
  'Beklemede': 'bg-gray-100 text-gray-700',
  'Müşteri Yanıtı Bekleniyor': 'bg-pink-100 text-pink-700',
  'Çözüldü': 'bg-emerald-100 text-emerald-700',
  'Kapatıldı': 'bg-red-100 text-red-700',
};

const priorityColors = {
  'Düşük': 'text-slate-500',
  'Normal': 'text-blue-600',
  'Yüksek': 'text-orange-600',
  'Acil': 'text-red-600',
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const res = await api.get('/tickets');
      setTickets(res.data);
    } catch (err) {}
    setLoading(false);
  };

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => !['Çözüldü', 'Kapatıldı'].includes(t.Status)).length,
    solved: tickets.filter(t => t.Status === 'Çözüldü').length,
    closed: tickets.filter(t => t.Status === 'Kapatıldı').length,
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 sm:p-8 text-white">
        <h1 className="text-2xl sm:text-3xl font-bold">Hoş Geldiniz, {user?.fullName}!</h1>
        <p className="text-blue-100 mt-2">Destek taleplerinizi buradan takip edebilirsiniz.</p>
        <Link to="/new-ticket" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-white text-blue-700 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-lg">
          <PlusCircle size={20} /> Yeni Talep Oluştur
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Toplam', value: stats.total, icon: Ticket, color: 'from-blue-500 to-blue-600' },
          { label: 'Açık', value: stats.open, icon: Clock, color: 'from-amber-500 to-orange-600' },
          { label: 'Çözülen', value: stats.solved, icon: CheckCircle, color: 'from-emerald-500 to-green-600' },
          { label: 'Kapalı', value: stats.closed, icon: AlertCircle, color: 'from-red-500 to-rose-600' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
            <div className={`w-10 h-10 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-3`}>
              <stat.icon size={20} className="text-white" />
            </div>
            <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
            <p className="text-sm text-slate-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Tickets */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Son Talepler</h2>
          <Link to="/new-ticket" className="text-sm text-blue-600 hover:underline font-medium">Tümünü Gör</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <Ticket size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Henüz destek talebiniz yok</p>
              <Link to="/new-ticket" className="inline-flex items-center gap-2 mt-3 text-blue-600 font-medium hover:underline">
                <PlusCircle size={16} /> İlk talebinizi oluşturun
              </Link>
            </div>
          ) : tickets.slice(0, 10).map(ticket => (
            <Link key={ticket.Id} to={`/tickets/${ticket.Id}`} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors group">
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-slate-400">#{ticket.Id}</span>
                  <h3 className="text-sm font-medium text-slate-800 truncate">{ticket.Title}</h3>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${statusColors[ticket.Status] || 'bg-slate-100 text-slate-600'}`}>{ticket.Status}</span>
                  <span className="text-xs text-slate-400">{ticket.Category}</span>
                  <span className={`text-xs font-medium ${priorityColors[ticket.Priority] || ''}`}>{ticket.Priority}</span>
                  {ticket.AssignedToName && <span className="text-xs text-slate-500">Atanan: {ticket.AssignedToName}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">{new Date(ticket.CreatedAt).toLocaleDateString('tr-TR')}</span>
                <ArrowRight size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
